import React from 'react';
import TutorAssignmentDropdown from './TutorAssignmentDropdown';
import MatchmakingButton from './MatchmakingButton';
import { getStudentDisplayName } from '../../utils/studentName';

/**
 * StudentTableRow
 *
 * Renders a single row in the student roster table.
 * Displays avatar, student info, subjects, tutor, sessions, date, and actions.
 */
const StudentTableRow = ({
  student,
  tutorName,
  isSelected,
  onToggleSelection,
  onViewDetails,
  isAllMode = false,
  isAdmin = false,
  tutors = [],
  onTutorAssignment,
  onStartMatch,
  isMatchingActive = false,
  matchingStudentUuid = null
}) => {
  // Parse subjects and limit display
  const subjects = student.target_subjects
    ? student.target_subjects.split(',').map(s => s.trim())
    : [];
  const displaySubjects = subjects.slice(0, 2);
  const remainingCount = subjects.length - 2;

  // Get initials for avatar placeholder
  const getInitials = () => {
    const firstInitial = (student.first_name || '').trim().charAt(0);
    const lastInitial = (student.last_name || '').trim().charAt(0);
    const initials = `${firstInitial}${lastInitial}`.trim().toUpperCase();
    if (initials) return initials;

    // Fallback to grade number
    if (student.grade_year_level) {
      const gradeMatch = student.grade_year_level.match(/\d+/);
      return gradeMatch ? gradeMatch[0] : '?';
    }
    return '?';
  };

  // Get display name
  const getDisplayName = () => {
    return getStudentDisplayName(student);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Short student ID
  const shortId = student.uuid ? student.uuid.substring(0, 8) : 'Unknown';

  return (
    <tr className={`roster-table__row ${isSelected ? 'roster-table__row--selected' : ''}`}>
      {/* Checkbox */}
      <td className="roster-table__cell roster-table__cell--checkbox">
        <label className="roster-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelection}
            aria-label={`Select student ${shortId}`}
          />
          <span className="roster-checkbox__mark"></span>
        </label>
      </td>

      {/* Student (Avatar + Name) */}
      <td className="roster-table__cell roster-table__cell--student">
        <div className="roster-table__student">
          {student.profile_image ? (
            <img
              src={student.profile_image}
              alt=""
              className="roster-table__avatar"
            />
          ) : (
            <div className="roster-table__avatar roster-table__avatar--placeholder">
              {getInitials()}
            </div>
          )}
          <div className="roster-table__student-info">
            <span className="roster-table__student-name">
              {getDisplayName()}
            </span>
            <span className="roster-table__student-grade">
              {student.grade_year_level || '-'}
            </span>
          </div>
          <button
            className="roster-table__external-link"
            onClick={onViewDetails}
            aria-label="View student details"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M5.5 3H3.5C2.94772 3 2.5 3.44772 2.5 4V10.5C2.5 11.0523 2.94772 11.5 3.5 11.5H10C10.5523 11.5 11 11.0523 11 10.5V8.5M8 2.5H11.5M11.5 2.5V6M11.5 2.5L6 8"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </td>

      {/* Subjects */}
      <td className="roster-table__cell roster-table__cell--subjects">
        <div className="roster-table__subjects">
          {displaySubjects.length > 0 ? (
            <>
              {displaySubjects.map((subject, idx) => (
                <span key={idx} className="roster-table__subject-badge">
                  {subject}
                </span>
              ))}
              {remainingCount > 0 && (
                <span className="roster-table__subject-badge roster-table__subject-badge--more">
                  +{remainingCount}
                </span>
              )}
            </>
          ) : (
            <span className="roster-table__empty-cell">-</span>
          )}
        </div>
      </td>

      {/* Tutor */}
      <td className="roster-table__cell roster-table__cell--tutor">
        {isAdmin && isAllMode ? (
          student.assigned_tutor_id ? (
            // Student has tutor - show dropdown for reassignment
            <TutorAssignmentDropdown
              studentUuid={student.uuid}
              currentTutorId={student.assigned_tutor_id}
              tutors={tutors}
              onAssignment={onTutorAssignment}
            />
          ) : (
            // Student unassigned - show both matchmaking button AND manual assignment dropdown
            <div className="roster-table__tutor-options">
              <MatchmakingButton
                onClick={() => onStartMatch && onStartMatch(student)}
                disabled={isMatchingActive}
                isLoading={matchingStudentUuid === student.uuid}
              />
              <TutorAssignmentDropdown
                studentUuid={student.uuid}
                currentTutorId={null}
                tutors={tutors}
                onAssignment={onTutorAssignment}
              />
            </div>
          )
        ) : tutorName ? (
          <span className="roster-table__tutor-name">{tutorName}</span>
        ) : isAllMode ? (
          <span className="roster-table__unassigned-badge">Unassigned</span>
        ) : (
          '-'
        )}
      </td>

      {/* Sessions */}
      <td className="roster-table__cell roster-table__cell--sessions">
        <span className="roster-table__session-count">
          {student.session_count || 0}
        </span>
      </td>

      {/* Assigned Date */}
      <td className="roster-table__cell roster-table__cell--date">
        {formatDate(student.assigned_at)}
      </td>

      {/* Actions */}
      <td className="roster-table__cell roster-table__cell--actions">
        <button
          className="roster-table__details-btn"
          onClick={onViewDetails}
        >
          Details
        </button>
      </td>
    </tr>
  );
};

export default StudentTableRow;
